import { Input, Text, InputWrapperProps, Grid } from "@mantine/core";
import {
  UseFormSetValue,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
} from "react-hook-form";
import { useParams } from "react-router-dom";
import Select from "src/components/shared/_FormInputs/Select";
import Switch from "src/components/shared/_FormInputs/Switch";
import { useCheckPerm, useWindowSize } from "src/hooks";
import { InputStyledWrapper } from "src/styled/UserForm.styled";
import { type IUserForm } from "src/pages/Settings/CrewSetUp/UserForm/UserForm.types";
import useNonMaritime from "../../../../../hooks/useNonMaritime";
import type { ComboboxItem } from "@mantine/core";
import { useEffect } from "react";

interface Props {
  register: UseFormRegister<IUserForm>;
  control: Control<IUserForm>;
  setValue: UseFormSetValue<IUserForm>;
  onTypeChange: (type: string) => void;
  types: ComboboxItem[];
  ranks: ComboboxItem[];
  errors: FieldErrors<IUserForm>;
}

const InputWrapper = (props: InputWrapperProps) => {
  return (
    <InputStyledWrapper style={{ width: "unset" }} {...props}>
      {props.children}
    </InputStyledWrapper>
  );
};

const Col = (props: any) => {
  return (
    <Grid.Col span={6} {...props}>
      <InputWrapper>{props.children}</InputWrapper>
    </Grid.Col>
  );
};

const Spacer = (props: any) => <Grid.Col span={6} {...props} />;

const Details = ({
  register,
  control,
  setValue,
  onTypeChange,
  types,
  ranks,
  errors, // onRankChange,
}: Props) => {
  const { nonMaritime, replaceMaritimeText } = useNonMaritime();
  const { userId } = useParams();
  const checkPerm = useCheckPerm();
  // const [temporalRank, setTemporalRank] = useState(false);
  // const [selectedTemporalRank, setSelectedTemporalRank] = useState<
  //   string | null
  // >(null);
  const disabled = !!(userId && !checkPerm("editeuser"));
  const userType = Number(useWatch({ control, name: "type" }));
  const { width } = useWindowSize();
  const lg = width ? width > 1400 && false : false;

  const deletedValue = useWatch({ control, name: "deleted" });

  useEffect(() => {
    Boolean(deletedValue) && setValue("active", false);
  }, [deletedValue]);

  return (
    <Grid gutter={lg ? "md" : "sm"} columns={lg ? 12 : 6}>
      <Col>
        <Text size={"sm"}>Type</Text>
        <Select
          name="type"
          control={control}
          data={types}
          onChange={onTypeChange}
          readOnly={disabled}
          required
          w={"50%"}
        />
      </Col>
      <Col>
        <Text size={"sm"}>Rank</Text>
        <Select
          name="role"
          control={control}
          data={ranks}
          readOnly={disabled}
          // onChange={onRankChange}
          required={userType !== 10}
          clearable={userType === 10}
          w={"50%"}
        />
      </Col>
      <Col>
        <Text size={"sm"}>External Ref Number</Text>
        <Input {...register("extref")} readOnly={disabled} w={"50%"} />
      </Col>
      <Spacer />
      {lg && <Spacer span={12} />}
      <Col>
        <Text size={"sm"}>First Name</Text>
        <Input
          {...register("fname", { required: true })}
          readOnly={disabled}
          error={!!errors?.fname}
          w={"50%"}
        />
      </Col>
      <Col>
        <Text size={"sm"}>Surname</Text>
        <Input
          {...register("sname", { required: true })}
          readOnly={disabled}
          error={!!errors?.sname}
          w={"50%"}
        />
      </Col>
      <Col>
        <Text size={"sm"}>Email</Text>
        <Input
          {...register("email", { required: true })}
          readOnly={disabled}
          error={!!errors?.email}
          w={"50%"}
        />
      </Col>
      <Col>
        <Text size={"sm"}>Personal Email</Text>
        <Input
          {...register("personalEmail")}
          readOnly={disabled}
          error={!!errors?.personalEmail}
          w={"50%"}
        />
      </Col>
      <Col>
        <Text size={"sm"}>Mobile</Text>
        <Input {...register("mobile")} readOnly={disabled} w={"50%"} />
      </Col>
      <Spacer />
      {lg && <Spacer span={12} />}
      {!userId && userType !== 10 && (
        <>
          <Col>
            <Text size={"sm"}>Password</Text>
            <Input {...register("pass")} type="password" w={"50%"} />
          </Col>
          <Col>
            <Text size={"sm"}>Pin</Text>
            <Input {...register("pin")} type="password" w={"50%"} />
          </Col>
          <Col>
            <Text size={"sm"}>Passphrase</Text>
            <Input {...register("passphrase")} type="password" w={"50%"} />
          </Col>
          <Spacer />
        </>
      )}
      {lg && userType !== 10 && !userId && <Spacer span={12} />}
      {!nonMaritime && userType !== 10 && (
        <>
          <Col span={lg ? 12 : 6}>
            <Text size={"sm"}>Medical Officer</Text>
            <Switch name="medofficer" control={control} disabled={disabled} />
          </Col>
        </>
      )}
      <Col span={lg ? 12 : 6}>
        <Text size={"sm"}>
          {replaceMaritimeText("Onboard")} /{" "}
          {replaceMaritimeText("Rotational Leave")}
        </Text>
        <Switch
          name="active"
          control={control}
          disabled={disabled || Boolean(deletedValue)}
        />
      </Col>
      {/* ------TEMPORARY RANK SELECTOR---- Chris to add feature on BE (VU2PD-72) */}
      {/* <div style={{ display: "flex", flexDirection: "column" }}> */}
      {/*   <InputWrapper > */}
      {/*     <Text size={"sm"}>Temporary Rotate Rank</Text> */}
      {/*     <MantineSwitch */}
      {/*       checked={temporalRank} */}
      {/*       onChange={e => setTemporalRank(e.currentTarget.checked)} */}
      {/*       disabled={disabled} */}
      {/*     /> */}
      {/*    */}
      {/*   {temporalRank && ( */}
      {/*     <InputWrapper > */}
      {/*       <Text size={"sm"}>Temporary Rank</Text> */}
      {/*       <MantineSelect */}
      {/*         w={"70%"} */}
      {/*         value={selectedTemporalRank} */}
      {/*         onChange={setSelectedTemporalRank} */}
      {/*         data={ranks} */}
      {/*         readOnly={disabled} */}
      {/*         required */}
      {/*       /> */}
      {/*      */}
      {/*   )} */}
      {/* </div> */}
      <Col span={lg ? 12 : 6}>
        <Text size={"sm"}>Archived</Text>
        <Switch name="deleted" control={control} disabled={disabled} />
      </Col>
    </Grid>
  );
};

export default Details;
